// Read Mixpanel token dynamically from environment
export const MIXPANEL_TOKEN = (process.env.REACT_APP_MIXPANEL_TOKEN || "").trim();

let isInitialized = false;

/**
 * Dynamically loads and initializes Mixpanel web SDK using the .env token
 */
export const initMixpanel = () => {
  if (typeof window === "undefined") return false;
  if (!MIXPANEL_TOKEN || MIXPANEL_TOKEN === "disabled") {
    return false;
  }

  if (isInitialized || (window.mixpanel && window.mixpanel.__loaded)) {
    return true;
  }

  // Official Mixpanel async stub loader
  (function (f, b) {
    if (!b.__SV) {
      var e, g, i, h;
      window.mixpanel = b;
      b._i = [];
      b.init = function (e, f, c) {
        function g(a, d) {
          var b = d.split(".");
          if (b.length === 2) {
            a = a[b[0]];
            d = b[1];
          }
          a[d] = function () {
            a.push([d].concat(Array.prototype.slice.call(arguments, 0)));
          };
        }
        var a = b;
        if (typeof c !== "undefined") {
          a = b[c] = [];
        } else {
          c = "mixpanel";
        }
        a.people = a.people || [];
        a.toString = function (a) {
          var d = "mixpanel";
          if (c !== "mixpanel") d += "." + c;
          if (!a) d += " (stub)";
          return d;
        };
        a.people.toString = function () {
          return a.toString(1) + ".people (stub)";
        };
        i = "disable time_event track track_pageview track_links track_forms track_with_groups add_group set_group remove_group register register_once alias unregister identify name_tag set_config reset opt_in_tracking opt_out_tracking has_opted_in_tracking has_opted_out_tracking clear_opt_in_out_tracking start_batch_senders people.set people.set_once people.unset people.increment people.append people.union people.track_charge people.clear_charges people.delete_user people.remove".split(" ");
        for (h = 0; h < i.length; h++) g(a, i[h]);
        var j = "set set_once union unset remove delete".split(" ");
        a.get_group = function () {
          function b(c) {
            d[c] = function () {
              var call2_args = arguments;
              var call2 = [c].concat(Array.prototype.slice.call(call2_args, 0));
              a.push([e, call2]);
            };
          }
          for (var d = {}, e = ["get_group"].concat(Array.prototype.slice.call(arguments, 0)), c = 0; c < j.length; c++) b(j[c]);
          return d;
        };
        b._i.push([e, f, c]);
      };
      b.__SV = 1.2;
      e = f.createElement("script");
      e.id = "mixpanel-js-snippet";
      e.type = "text/javascript";
      e.async = true;
      e.src = "https://cdn.mxpnl.com/libs/mixpanel-2-latest.min.js";
      g = f.getElementsByTagName("script")[0];
      if (g && g.parentNode) {
        g.parentNode.insertBefore(e, g);
      } else {
        document.head.appendChild(e);
      }
    }
  })(document, window.mixpanel || []);

  window.mixpanel.init(MIXPANEL_TOKEN, {
    autotrack: false, // Don't autotrack general clicks
    track_pageview: false, // Don't track pageviews (reserved for GA4)
    persistence: "localStorage",
  });

  isInitialized = true;
  return true;
};

/**
 * Track user login in Mixpanel
 * @param {object} params - { email, method, organizationId, role }
 */
export const trackLogin = (params = {}) => {
  if (!MIXPANEL_TOKEN || MIXPANEL_TOKEN === "disabled") return;
  initMixpanel();

  try {
    if (window.mixpanel) {
      if (params.email) {
        window.mixpanel.identify(params.email);
        window.mixpanel.people.set({
          $email: params.email,
          role: params.role || undefined,
          organizationId: params.organizationId || undefined,
          last_login: new Date().toISOString(),
        });
      }

      window.mixpanel.track("Login", {
        method: params.method || "Email/Password",
        email: params.email,
        organizationId: params.organizationId,
        role: params.role,
        timestamp: new Date().toISOString(),
      });
    }
  } catch (error) {
    console.error("Mixpanel trackLogin error:", error);
  }
};

/**
 * Track user signup in Mixpanel
 * @param {object} params - { email, firstName, lastName, method, organizationId, role }
 */
export const trackSignup = (params = {}) => {
  if (!MIXPANEL_TOKEN || MIXPANEL_TOKEN === "disabled") return;
  initMixpanel();

  try {
    if (window.mixpanel) {
      if (params.email) {
        window.mixpanel.identify(params.email);
        const fullName = `${params.firstName || ""} ${params.lastName || ""}`.trim();
        window.mixpanel.people.set({
          $email: params.email,
          $first_name: params.firstName || undefined,
          $last_name: params.lastName || undefined,
          $name: fullName || undefined,
          role: params.role || undefined,
          organizationId: params.organizationId || undefined,
          created_at: new Date().toISOString(),
        });
      }

      window.mixpanel.track("Sign Up", {
        method: params.method || "Email/Password",
        email: params.email,
        name: `${params.firstName || ""} ${params.lastName || ""}`.trim() || undefined,
        organizationId: params.organizationId,
        role: params.role,
        timestamp: new Date().toISOString(),
      });
    }
  } catch (error) {
    console.error("Mixpanel trackSignup error:", error);
  }
};

/**
 * Track a generic product event in Mixpanel.
 * Never throws and is a no-op when the token is missing or "disabled".
 * Keep properties to ids/counts/enums; do not pass post content or other free text.
 * @param {string} eventName - Event name, e.g. "Post Created"
 * @param {object} [properties] - Extra event properties
 */
export const trackEvent = (eventName, properties = {}) => {
  if (!MIXPANEL_TOKEN || MIXPANEL_TOKEN === "disabled") return;
  initMixpanel();

  try {
    if (window.mixpanel) {
      window.mixpanel.track(eventName, {
        ...properties,
        timestamp: new Date().toISOString(),
      });
    }
  } catch (error) {
    console.error(`Mixpanel trackEvent error (${eventName}):`, error);
  }
};

/**
 * Clear the Mixpanel identity (call on logout so the next user is not merged with this one)
 */
export const resetMixpanel = () => {
  if (!MIXPANEL_TOKEN || MIXPANEL_TOKEN === "disabled") return;

  try {
    if (window.mixpanel && typeof window.mixpanel.reset === "function") {
      window.mixpanel.reset();
    }
  } catch (error) {
    console.error("Mixpanel reset error:", error);
  }
};

const mixpanelService = {
  MIXPANEL_TOKEN,
  initMixpanel,
  trackLogin,
  trackSignup,
  trackEvent,
  resetMixpanel,
};

export default mixpanelService;

